"use client"

import {
  ChannelEmptyPlaceholder,
  ChannelInner,
  ChannelLoadingSkeleton,
  CreateChannel,
  MessagesSidebar,
  MessagesSkeleton,
  ThreadHeader,
  TypingIndicator,
} from "@/components/Messaging"
import { ArrowUpCircleIcon, PaperClipIcon } from "@heroicons/react/24/outline"
import clsx from "clsx"
import type { SendButtonProps } from "stream-chat-react"
import { Channel, Chat, useCreateChatClient } from "stream-chat-react"
import { useMessages } from "./hooks/useMessages"
import { AttachmentRenderer } from "./AttachmentRenderer"

interface Props {
  filters: any
  userUuid: string
  messagingToken: string
  trackingEntryPoint: string
}

const apiKey = process.env.NEXT_PUBLIC_STREAM_CHAT_API_KEY!

export const fileUploadIcon = () => {
  return (
    <div>
      <PaperClipIcon height={26} className="stroke-fg-1" />
    </div>
  )
}

export const sendButton = ({ sendMessage, ...rest }: SendButtonProps) => {
  return (
    <div onClick={sendMessage} className="cursor-pointer">
      <ArrowUpCircleIcon height={36} width={36} className="stroke-primary-3 hover:stroke-fg-1" />
    </div>
  )
}

export const MessagesMainContent: React.FC<Props> = ({ filters, userUuid, messagingToken, trackingEntryPoint }) => {
  const { showMobileChannel, isCreating } = useMessages()

  const client = useCreateChatClient({
    apiKey,
    tokenOrProvider: messagingToken,
    userData: { id: userUuid },
  })

  if (!client) return <MessagesSkeleton />

  return (
    <Chat client={client}>
      {/* Sidebar */}
      <div
        className={clsx(
          "transition-width duration-200 ease-in-out",
          "flex h-full flex-shrink-0 overflow-hidden bg-bg-1",
          "w-screen md:w-[420px]"
        )}>
        <MessagesSidebar
          channelListOptions={{
            filters,
            sort: { last_message_at: -1 },
            options: { watch: true, state: true, limit: 10 },
          }}
        />
      </div>

      {/* Channel */}
      <div
        className={clsx(
          "md:z-1 absolute top-0 z-50 flex h-dvh w-screen flex-col overflow-y-hidden bg-bg-1 transition-transform duration-300 ease-in-out md:relative md:w-full md:translate-x-0",
          showMobileChannel ? "messages-mobile-slide-in-left active" : "messages-mobile-slide-in-left"
        )}>
        <div className="flex-grow-1 relative flex h-full w-full flex-col">
          <Channel
            ThreadHead={ThreadHeader}
            maxNumberOfFiles={10}
            multipleUploads={true}
            FileUploadIcon={fileUploadIcon}
            SendButton={sendButton}
            EmptyPlaceholder={<ChannelEmptyPlaceholder />}
            EmptyStateIndicator={ChannelEmptyPlaceholder}
            LoadingIndicator={ChannelLoadingSkeleton}
            TypingIndicator={TypingIndicator}
            Attachment={AttachmentRenderer}>
            {isCreating ? <CreateChannel /> : <ChannelInner />}
          </Channel>
        </div>
      </div>
    </Chat>
  )
}
